'use client';

import Link from 'next/link';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';

/**
 * NavLink — Figma komponent „Nav Link" (8:19).
 *
 * Popis z Figmy: „Odkaz v hlavnej navigácii. State: Default / Hover (akcent) /
 * Active (akcentová bodka)."
 *
 * Typografiu drží trieda `.nav-link` v headings.css (Nav/Link — Medium 500,
 * 14 → 16, riadkovanie 24, prekladanie Tight-05). Tu sa rieši už len stav.
 *
 * Aktívna položka má plnú sýtosť a 6px bodku v značkovej modrej; neaktívne
 * bežia na 70 % krytia. Bodka je obyčajný `<span>`, nie ikona — je to kruh
 * v jednej farbe, súbor by pre ňu bol zbytočný.
 *
 * Farby: jediná premenná je `primary` (#1A5FD6), zvyšok hexom.
 */
/**
 * Zvyšné props sa prelievajú na samotný odkaz. Je to nutné pre `SheetClose asChild`
 * (mobilné menu): Radix cezeň posiela `onClick` a `ref`, ktorými panel po kliknutí
 * zatvára. Bez preliatia by kliknutie na položku menu panel nechalo otvorený.
 */
export interface NavLinkProps extends Omit<React.ComponentPropsWithRef<'a'>, 'href' | 'title'> {
  title: string;
  href: string;
  active?: boolean;
  className?: string;
}

export function NavLink({ title, href, active = false, className, ref, ...rest }: NavLinkProps) {
  const safeHref = getSafeHref(href);
  const Comp = isRelativeHref(safeHref) ? Link : 'a';

  return (
    <Comp
      {...rest}
      ref={ref as any}
      href={safeHref}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'ea-nav-link',
        'nav-link inline-flex items-center gap-2 whitespace-nowrap text-[#212121] transition-opacity',
        'hover:text-primary hover:opacity-100',
        active ? 'opacity-100' : 'opacity-70',
        className,
      )}
    >
      {title}
      {active && (
        <span
          aria-hidden="true"
          className="ea-nav-link__dot size-[6px] shrink-0 rounded-full bg-primary"
        />
      )}
    </Comp>
  );
}
